import useOptimisticListPick from '../../../../../../../../components/ListPicker/useOptimisticListPick';
import { SelectedData } from '../../../../../../../../components/ListPicker/useOptimisticListPick.types';
import { PackQuantitiesWithMultiplier } from '../../../../../../../../services/modules/product-colors-packs/product-colors-packs.types';
import { productColorPacksRepositoryOfflineAdapters } from '../../../../../../../offline/offlineRequestAdapters/productColorPacks.repository.offlineAdapters';
import { UsedInOfflineMode } from '../../../../../../../offline/types/UsedInOfflineMode';
import { createParamsToUseOptimisticListPickWhenOffline } from '../../../../../../../offline/utils/createParamsToUseOptimisticListPickWhenOffline';
import { AvailabilityEnum } from '../../../../../../../products/ProductTypes';
import { OrderProductSelectedPack } from '../../../../../../../products/details/productDetails.types';
import { getPacksWithQuantitiesFromSelectedPacks } from '../../orderProductGrid/utils/commonGrids.utils';

interface UseSearchOrderProductPacksProps extends UsedInOfflineMode {
  readMode: boolean;
  selectedPacks: OrderProductSelectedPack[];
  productColorId: string;
  productColorAvailability: AvailabilityEnum;
  orderId?: string;
}

export const useSearchOrderProductPacks = ({
  readMode,
  isOfflineMode,
  selectedPacks,
  productColorId,
  productColorAvailability,
  orderId,
}: UseSearchOrderProductPacksProps) => {
  const defaultValue = selectedPacks.map(({ packId }) => packId);

  const {
    isLoading,
    isLoadingInitialValues,
    loadMore,
    onChangeSelectedData,
    onUpdateSelectedItem,
    onSearch,
    options,
    selectedData,
  } = useOptimisticListPick<PackQuantitiesWithMultiplier>(
    createParamsToUseOptimisticListPickWhenOffline({
      isOnline: !isOfflineMode,
      offlineFetcher: (params) =>
        productColorPacksRepositoryOfflineAdapters.pageSelectedPacks({
          ...params,
          productColorAvailability,
          productColorId,
        }),
      offlineFetcherToInitialValues: (ids) =>
        productColorPacksRepositoryOfflineAdapters.pageSelectedPacks({
          ids,
          skip: 0,
          limit: ids.length,
          productColorId,
          productColorAvailability,
        }),
      onlineProps: {
        defaultValue: readMode ? [] : defaultValue,
        listPickProps: {
          url: `product-colors/${productColorId}/selected-packs/${productColorAvailability}`,
          queryField: 'packName',
          disabled: readMode,
          defaultQuery: { orderId },
        },
        getInitialValuesQuery: {
          withInvalids: true,
          orderId,
        },
      },
    }),
  );

  const optionsWithQuantities: PackQuantitiesWithMultiplier[] = options.map((selectedPack) => {
    const selected = selectedPacks.find((pack) => pack.packId === selectedPack.id);
    return {
      ...selectedPack,
      multiplier: selected?.quantity,
    };
  });

  const currentItemsWithQuantity: PackQuantitiesWithMultiplier[] = selectedData.currentItems.map(
    (selectedPack) => {
      const selected = selectedPacks.find((pack) => pack.packId === selectedPack.id);
      return {
        ...selectedPack,
        multiplier: selectedPack.multiplier || selected?.quantity,
      };
    },
  );

  const selectedDataWithQuantities: SelectedData<PackQuantitiesWithMultiplier> = {
    ...selectedData,
    currentItems: currentItemsWithQuantity,
  };

  const readModeOptions = getPacksWithQuantitiesFromSelectedPacks(selectedPacks);

  return {
    isLoading: options.length === 0 && isLoading,
    isLoadingInitialValues,
    loadMore,
    onChangeSelectedData,
    onUpdateSelectedItem,
    onSearch,
    options: readMode ? readModeOptions : optionsWithQuantities,
    selectedData: selectedDataWithQuantities,
  };
};
